import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, X } from 'lucide-react';

// An informational notice, not a consent banner. It used to offer «Aceptar» / «Solo esenciales»,
// but nothing on the site depended on the answer: there is nothing optional to accept (no
// analytics, no advertising, fonts served from somostorre.com). A choice that changes nothing
// is misleading, so the notice now says what is kept on the device and why, with one button.
//
// The key is the old one on purpose: whoever closed the old banner is not asked again, the app
// shell (mobile SharedWebView) and the test harnesses set it, and Legal.js lists it. Any value
// means "already seen".
export const NOTICE_KEY = 'torre_cookie_consent';
const SEEN = 'seen';

// Storage can be blocked; this runs on every page, so a throw here was a white page.
function readSeen() {
  try { return localStorage.getItem(NOTICE_KEY); } catch { return null; }
}
function saveSeen() {
  try { localStorage.setItem(NOTICE_KEY, SEEN); } catch { /* storage unavailable: shown again next visit */ }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const hasBeenShown = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Not in the native app: the shell hides it (and it is not needed there, see Legal.js).
    if (window.__isNativeApp) return;

    if (!readSeen()) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  // Broadcast visibility so other components can react (CityAssistant hides its bubble while the
  // notice is up) — only after the notice has been shown.
  useEffect(() => {
    if (visible) {
      hasBeenShown.current = true;
    }
    if (hasBeenShown.current) {
      window.dispatchEvent(new CustomEvent('cookie-banner-visibility', { detail: { visible } }));
    }
  }, [visible]);

  const dismiss = () => {
    saveSeen();
    setVisible(false);
  };

  const moreInfo = () => {
    dismiss();
    navigate('/legal?tab=cookies');
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-20 md:bottom-4 left-4 right-4 z-40 max-w-lg mx-auto animate-fade-in-up" role="region" aria-label="Lo que guardamos en tu dispositivo" data-testid="cookie-consent-banner">
      <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-torre-teal/10 flex items-center justify-center shrink-0 mt-0.5">
            <Shield size={16} className="text-torre-teal" aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-torre-slate mb-1">Lo que guardamos en tu dispositivo</p>
            <p className="text-xs text-slate-500 leading-relaxed" data-testid="cookie-notice-text">
              SomosTorre guarda en este navegador solo lo necesario para funcionar: tu sesión si entras, tus preferencias de pantalla, tus favoritos y los borradores de lo que escribes. El proveedor que aloja la web pone además dos cookies suyas. No hay publicidad, analítica ni seguimiento.
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <button onClick={dismiss}
                className="px-4 py-2 rounded-full bg-torre-teal text-white text-xs font-bold hover:bg-torre-teal-hover transition-colors"
                data-testid="cookie-dismiss-btn">
                Entendido
              </button>
              <button onClick={moreInfo}
                className="px-3 py-2 text-torre-teal hover:underline text-xs font-bold"
                data-testid="cookie-more-info">
                Más información
              </button>
            </div>
          </div>
          <button onClick={dismiss} aria-label="Cerrar aviso" className="text-slate-500 hover:text-slate-700 transition-colors shrink-0" data-testid="cookie-close-btn">
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
